import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "drop-down__content__wrapper" }
const _hoisted_2 = {
  key: 0,
  class: "drop-down-menu-header",
  "data-test": "drop-down-menu-header"
}
const _hoisted_3 = {
  key: 3,
  class: "drop-down-menu-footer",
  "data-test": "drop-down-menu-footer"
}
const _hoisted_4 = { class: "body--body-01 s-p-4 tw-text-core-neutral-cold-50" }

import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import DropDownMenuGroupItem from 'frontcore/views/drop-down/views/unit/DropDownMenuGroupItem.vue'
import i18n from 'frontcore/i18n'
import useDropDownGroupedItems from 'frontcore/views/drop-down/domain/useDropDownGroupedItems'
import { computed, inject, onUnmounted, ref, toRef, useSlots, watch } from 'vue'
import { DROPDOWN_REM_WIDTH } from 'frontcore/views/drop-down/domain/constants'
import {
  DropDownDirection,
  DropDownMenuItemProps,
} from "frontcore/views/drop-down/domain/types"
import { dropDownMenuScrollKey } from 'frontcore/views/drop-down/views/base/types'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'

interface Props {
  optionList: DropDownMenuItemProps[]
  itemSize?: number
  direction?: DropDownDirection
  isSimpleList?: boolean
  width?: number
  keepOrder?: boolean
}

export default /*@__PURE__*/_defineComponent({
  __name: 'DropDownMenuBase',
  props: {
    optionList: {},
    itemSize: { default: 44 },
    direction: { default: DropDownDirection.LEFT },
    isSimpleList: { type: Boolean, default: false },
    width: { default: DROPDOWN_REM_WIDTH },
    keepOrder: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props

const slots = useSlots()

const directionClass = computed(() => {
  return {
    'drop-down-menu--left': props.direction === DropDownDirection.LEFT,
    'drop-down-menu--right': props.direction === DropDownDirection.RIGHT,
  }
})

const dropdownWidth = props.width ?? DROPDOWN_REM_WIDTH

const { t } = i18n.global

const optionList = toRef(props, 'optionList')
const keepOrder = toRef(props, 'keepOrder')
const { groupedOptionList } = useDropDownGroupedItems(optionList, keepOrder)

const dynamicKeyActive = ref(true)

const dynamicKey = computed(() => `refresh-${dynamicKeyActive.value}`)

const scrollerRef = ref<InstanceType<typeof DynamicScroller>>(null)
const scrollToItemHandler = async (itemId: DropDownMenuItemProps['id']) => {
  await new Promise((r) => setTimeout(r, 0)) // https://github.com/Akryum/vue-virtual-scroller/issues/307#issuecomment-1042320227
  const index = props.optionList.findIndex(({ id }) => id === itemId)
  if (index > 0) {
    scrollerRef.value.scrollToItem(index - 1)
  }
}
const emitter = inject(dropDownMenuScrollKey, null)
emitter?.on('scrollToItemId', scrollToItemHandler)
onUnmounted(() => {
  emitter?.off('scrollToItemId', scrollToItemHandler)
})

watch(() => props.optionList.length, () => {
  dynamicKeyActive.value = !dynamicKeyActive.value
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("ul", {
      ref: "dropDownContent",
      "data-test": "dropdown-content",
      class: _normalizeClass(["drop-down__content tw-bg-core-neutral-warm-100 tw-rounded tw-m-0", directionClass.value]),
      style: _normalizeStyle({ minWidth: `${_unref(dropdownWidth)}rem` })
    }, [
      (_unref(slots).header && _unref(slots).header())
        ? (_openBlock(), _createElementBlock("li", _hoisted_2, [
            _renderSlot(_ctx.$slots, "header")
          ]))
        : _createCommentVNode("", true),
      (_ctx.isSimpleList)
        ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(_unref(groupedOptionList), (item, idx) => {
            return (_openBlock(), _createElementBlock("li", {
              key: `option-list-${idx}`
            }, [
              (item?.showGroupLabel)
                ? (_openBlock(), _createBlock(DropDownMenuGroupItem, {
                    key: 0,
                    group: item?.group
                  }, null, 8, ["group"]))
                : _createCommentVNode("", true),
              _renderSlot(_ctx.$slots, "item", { item: item })
            ]))
          }), 128))
        : (_openBlock(), _createBlock(_unref(DynamicScroller), {
            ref_key: "scrollerRef",
            ref: scrollerRef,
            key: dynamicKey.value,
            items: _unref(groupedOptionList),
            minItemSize: _ctx.itemSize,
            class: "drop-down-menu__scroller"
          }, {
            default: _withCtx(({ item, active, index }) => [
              _createVNode(_unref(DynamicScrollerItem), {
                item: item,
                active: active,
                "data-index": index,
                "data-test": `drop-down-content-item-${index}`,
                sizeDependencies: [
            item?.showGroupLabel,
          ]
              }, {
                default: _withCtx(() => [
                  (item?.showGroupLabel)
                    ? (_openBlock(), _createBlock(DropDownMenuGroupItem, {
                        key: 0,
                        group: item?.group
                      }, null, 8, ["group"]))
                    : _createCommentVNode("", true),
                  _renderSlot(_ctx.$slots, "item", { item: item })
                ], undefined, true),
                _: 2
              }, 1032, ["item", "active", "data-index", "data-test", "sizeDependencies"])
            ]),
            _: 3
          }, 8, ["items", "minItemSize"])),
      (_unref(slots).footer && _unref(slots).footer())
        ? (_openBlock(), _createElementBlock("li", _hoisted_3, [
            _renderSlot(_ctx.$slots, "footer")
          ]))
        : _createCommentVNode("", true)
    ], 6),
    (optionList.value.length==0)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["drop-down__content tw-bg-core-neutral-warm-100 tw-rounded tw-m-0", directionClass.value])
        }, [
          _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(t)('common.messages.no_results')), 1)
        ], 2))
      : _createCommentVNode("", true)
  ]))
}
}

})